/**
 * AppLayout Component
 *
 * Vercel-inspired two-row header layout:
 * - Row 1: Logo / Org switcher / User
 * - Row 2: Tab navigation
 */

import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from '@heroui/react';
import { OrganizationSwitcher, useUser } from '@clerk/clerk-react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  ChevronDown,
  CreditCard,
  Key,
  LogOut,
  Menu,
  Moon,
  Sun,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/design-system';
import { useAuth } from '@/lib/auth';

interface AppLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { name: 'Overview', href: '/' },
  { name: 'Datasets', href: '/datasets' },
  { name: 'Training', href: '/training' },
  { name: 'Inference', href: '/inference' },
  { name: 'Settings', href: '/settings' },
];

function isActiveRoute(pathname: string, href: string): boolean {
  if (href === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(href);
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark', !isDarkMode);
    document.documentElement.classList.toggle('light', isDarkMode);
  };

  const userEmail = user?.emailAddresses[0]?.emailAddress;
  const userName = user?.fullName || user?.firstName || userEmail?.split('@')[0] || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      {/* Header Row 1: Brand + Org + User */}
      <header className="border-b border-default-100">
        <div className="flex h-12 items-center justify-between px-4">
          {/* Left: Logo + Org Switcher */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-md text-default-500 hover:bg-default-100 sm:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/relay-logo.png" alt="Relay" className="h-6 w-6" />
              <span className="text-sm font-semibold">Relay</span>
            </Link>

            {/* Slash separator */}
            <span className="hidden text-default-300 sm:inline">/</span>

            {/* Org Switcher */}
            <div className="hidden sm:block">
              <OrganizationSwitcher
                afterCreateOrganizationUrl="/"
                afterLeaveOrganizationUrl="/"
                afterSelectOrganizationUrl="/"
                afterSelectPersonalUrl="/"
                createOrganizationMode="modal"
                organizationProfileMode="modal"
                hidePersonal={false}
              />
            </div>
          </div>

          {/* Right: Theme + User */}
          <div className="flex items-center gap-1">
            {/* Dark mode toggle */}
            <Button
              isIconOnly
              variant="light"
              size="sm"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onPress={toggleDarkMode}
              className="h-11 w-11 min-w-0 text-default-500"
            >
              {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* User dropdown */}
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-default-100"
                >
                  <Avatar
                    size="sm"
                    showFallback
                    name={userInitial}
                    src={user?.imageUrl}
                    isBordered
                    classNames={{
                      base: 'h-6 w-6 ring-1 ring-default-200',
                      fallback: 'text-xs bg-default-100',
                    }}
                  />
                  <ChevronDown className="h-3 w-3 text-default-400" />
                </button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="User menu"
                variant="flat"
                classNames={{
                  base: 'bg-content1 border border-default-100 shadow-lg',
                }}
              >
                <DropdownSection showDivider>
                  <DropdownItem
                    key="profile"
                    className="h-12 gap-2 opacity-100"
                    textValue="Signed in"
                  >
                    <p className="text-xs text-default-500">Signed in as</p>
                    <p className="text-xs font-medium truncate max-w-[180px]">
                      {userEmail || 'Loading...'}
                    </p>
                  </DropdownItem>
                </DropdownSection>
                <DropdownSection showDivider>
                  <DropdownItem
                    key="settings"
                    href="/settings"
                    startContent={<User className="h-3.5 w-3.5" />}
                    className="text-xs"
                  >
                    Account Settings
                  </DropdownItem>
                  <DropdownItem
                    key="api-keys"
                    href="/settings/api-keys"
                    startContent={<Key className="h-3.5 w-3.5" />}
                    className="text-xs"
                  >
                    API Keys
                  </DropdownItem>
                  <DropdownItem
                    key="billing"
                    href="/settings/billing"
                    startContent={<CreditCard className="h-3.5 w-3.5" />}
                    className="text-xs"
                  >
                    Billing
                  </DropdownItem>
                </DropdownSection>
                <DropdownSection>
                  <DropdownItem
                    key="logout"
                    color="danger"
                    startContent={<LogOut className="h-3.5 w-3.5" />}
                    className="text-xs"
                    onPress={() => signOut()}
                  >
                    Sign Out
                  </DropdownItem>
                </DropdownSection>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {/* Header Row 2: Tab Navigation */}
        <nav className="hidden sm:block">
          <div className="flex h-10 items-end gap-0 px-4">
            {navItems.map((item) => {
              const isActive = isActiveRoute(location.pathname, item.href);

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'relative px-3 pb-2.5 text-sm transition-colors',
                    isActive
                      ? 'text-foreground'
                      : 'text-default-500 hover:text-foreground'
                  )}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      {/* Mobile Navigation Overlay */}
      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-72 bg-background sm:hidden">
          <div className="flex h-12 items-center justify-between border-b border-default-100 px-4">
            <Link to="/" className="flex items-center gap-2" onClick={() => setIsMenuOpen(false)}>
              <img src="/relay-logo.png" alt="Relay" className="h-6 w-6" />
              <span className="text-sm font-semibold">Relay</span>
            </Link>
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-md text-default-500 hover:bg-default-100"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4">
            {/* Mobile Org Switcher */}
            <div className="mb-4">
              <OrganizationSwitcher
                afterCreateOrganizationUrl="/"
                afterLeaveOrganizationUrl="/"
                afterSelectOrganizationUrl="/"
                afterSelectPersonalUrl="/"
              />
            </div>

            <div className="h-px bg-default-100 mb-4" />

            {/* Mobile Nav Items */}
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = isActiveRoute(location.pathname, item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      'block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-default-100 text-foreground'
                        : 'text-default-500 hover:bg-default-50 hover:text-foreground'
                    )}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>

            <div className="h-px bg-default-100 my-4" />

            {/* Mobile Sign Out */}
            <button
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                signOut();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger/10"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
        </>
      )}

      {/* Main content */}
      <main className="min-h-[calc(100vh-88px)]">
        <div className="animate-fade-in">{children}</div>
      </main>
    </div>
  );
}
